import { useState } from "react";
import {
  FileText,
  Loader2,
  Download,
  Paperclip,
  Plus,
  Files,
  Image as ImageIcon,
  FileSpreadsheet,
  FileIcon,
  X,
  Lock,
  AlertCircle,
} from "lucide-react";

import { useAuth } from "@/lib/auth-context";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useTarefaArquivos } from "@/hooks/tarefas/use-tarefa-arquivos";
import { TarefaUploadDialog } from "./arquivos/TarefaUploadDialog";
import { PermissaoEnum } from "@workflow/types";
import { usePermissions } from "@/hooks/use-permissions";
import { PermissionWrapper } from "../permissao/PermissionWrapper";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

interface TarefaArquivosCardProps {
  tarefaId: number;
  isConcluido: boolean;
  criadoPorId?: number;
  atribuidoAId?: number;
}

// O helper visual pode ficar aqui (é pura UI)
const getFileIcon = (nome: string) => {
  const extension = nome.split(".").pop()?.toLowerCase();

  if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(extension || "")) {
    return <ImageIcon className="h-4 w-4 text-purple-500" />;
  }

  if (["xls", "xlsx", "csv"].includes(extension || "")) {
    return <FileSpreadsheet className="h-4 w-4 text-emerald-500" />;
  }

  if (["pdf"].includes(extension || "")) {
    return <FileText className="h-4 w-4 text-red-500" />;
  }

  return <FileIcon className="h-4 w-4 text-primary" />;
};

export function TarefaArquivosCard({
  tarefaId,
  isConcluido,
  criadoPorId,
  atribuidoAId,
}: TarefaArquivosCardProps) {
  const { user } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    arquivos,
    isLoading,
    isUploading,
    uploadProgress,
    uploadArquivo,
    downloadArquivo,
    downloadingId,
    deleteArquivo,
    isDeleting,
  } = useTarefaArquivos(tarefaId);

  const { isAdmin } = usePermissions();

  // Controle de Permissão
  const isCriador = Number(user?.id) === Number(criadoPorId);
  const isAtribuido = Number(user?.id) === Number(atribuidoAId);

  const canUpload = isCriador || isAtribuido || isAdmin;
  const getRemoveAbility = (arq: any) => {
    if (isAdmin) return { canRemove: true };

    const isOwnerOrAssigned = isCriador || isAtribuido;
    if (!isOwnerOrAssigned || !arq.criadoEm) return { canRemove: false, reason: "Sem permissão" };

    const dataCriacao = new Date(arq.criadoEm).getTime();
    const agora = new Date().getTime();
    const umaHoraEmMs = 60 * 60 * 1000;
    const decorrido = agora - dataCriacao;

    const faltamMinutos = Math.ceil((umaHoraEmMs - decorrido) / (60 * 1000));

    if (decorrido < umaHoraEmMs) {
      return { canRemove: true, minutesLeft: faltamMinutos };
    }

    return { canRemove: false, reason: "Tempo expirado" };
  };

  return (
    <>
      <Card className="flex flex-col overflow-hidden border-border bg-card shadow-sm">
        <CardHeader className="border-b bg-muted/20 py-3">
          <CardTitle className="flex items-center justify-between text-sm font-semibold text-foreground/90">
            <div className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-primary" />
              Anexos da Tarefa
              {!isLoading && arquivos.length > 0 && (
                <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                  {arquivos.length}
                </span>
              )}
            </div>

            {canUpload && (
              <Button
                variant="ghost"
                size="sm"
                className="-mr-2 h-8 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 hover:text-primary"
                onClick={() => setIsDialogOpen(true)}
              >
                <Plus className="h-4 w-4" />{" "}
                <span className="hidden xl:ml-1.5 xl:inline">Anexar</span>
              </Button>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="custom-scrollbar relative max-h-[350px] min-h-[150px] flex-1 overflow-y-auto p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">
              <Skeleton className="h-14 w-full rounded-xl" />
              <Skeleton className="h-14 w-full rounded-xl" />
            </div>
          ) : arquivos.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center space-y-3 p-8 text-center duration-500 animate-in fade-in">
              <div className="rounded-full bg-muted p-3">
                <Files className="h-6 w-6 text-muted-foreground opacity-50" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Nenhum arquivo anexado</p>
                <p className="text-xs text-muted-foreground">
                  Documentos ou imagens de suporte aparecerão aqui.
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {arquivos?.map((arq) => {
                const { canRemove, reason, minutesLeft } = getRemoveAbility(arq);

                return (
                  <div
                    key={arq.id}
                    className="group flex items-center justify-between p-4 transition-all duration-300 animate-in slide-in-from-bottom-2 hover:bg-muted/30"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="shrink-0 rounded-lg border border-border bg-background p-2 transition-colors group-hover:border-primary/50 group-hover:bg-primary/5">
                        {getFileIcon(arq.nome)}
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span
                          className="truncate text-xs font-medium transition-colors group-hover:text-primary"
                          title={arq.nome}
                        >
                          {arq.nome}
                        </span>
                        <span className="mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                          Anexo
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 md:gap-2">
                      {canRemove ? (
                        <div className="flex items-center gap-2">
                          {!isAdmin && minutesLeft && minutesLeft <= 10 && (
                            <span className="animate-pulse text-[10px] font-medium text-amber-600">
                              {minutesLeft}min restando
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={downloadingId === arq.id || isDeleting}
                            className="h-8 w-8 shrink-0 rounded-full transition-all hover:bg-red-100 hover:text-red-500"
                            onClick={() => deleteArquivo(arq.id)}
                            title="Remover arquivo"
                          >
                            {isDeleting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="flex h-8 w-8 cursor-help items-center justify-center text-muted-foreground/50 opacity-30">
                                <X className="h-3.5 w-3.5" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent
                              side="left"
                              className="border-border bg-popover p-4 shadow-xl"
                            >
                              <div className="flex max-w-[220px] flex-col gap-2">
                                <div className="flex items-center gap-2 text-amber-500">
                                  <AlertCircle className="h-4 w-4" />
                                  <span className="text-xs font-bold uppercase tracking-tight">
                                    Ação Restrita
                                  </span>
                                </div>

                                <div className="space-y-2">
                                  <p className="text-xs leading-relaxed text-muted-foreground">
                                    {reason === "Tempo expirado" ? (
                                      <>
                                        A remoção é permitida apenas na{" "}
                                        <strong>primeira hora</strong> após o upload para mitigar
                                        casos de envio por engano.
                                      </>
                                    ) : (
                                      "Você não possui permissão para remover arquivos nesta tarefa."
                                    )}
                                  </p>

                                  {reason === "Tempo expirado" && (
                                    <p className="rounded-md border border-border/50 bg-muted/50 p-2 text-[11px] leading-tight text-foreground/70">
                                      Após esse período, o arquivo é fixado ao histórico. Para
                                      remoções críticas, entre em contato com o{" "}
                                      <strong>Administrador</strong>.
                                    </p>
                                  )}
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={downloadingId === arq.id}
                        className="h-8 w-8 rounded-full transition-all hover:bg-primary/10 hover:text-primary focus:opacity-100"
                        onClick={() => downloadArquivo(arq.id)}
                        title="Baixar arquivo"
                      >
                        {downloadingId === arq.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <TarefaUploadDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onUpload={uploadArquivo}
        isUploading={isUploading}
        progress={uploadProgress}
      />
    </>
  );
}
